import { useState, useCallback } from 'react';
import getAPI from "@API/index";
import { getAuthToken } from "@Security/stateHelpers";
import { REACT_APP_BACKEND_URL } from "@Constants/tokens";
import { getRequest } from "@State/actions/actionsGet/actionGetRequest";
import { REDUX_ACTION_TYPES } from '../constants';

export const useAccountEditorAPI = () => {
  const [loading, setLoading] = useState(false);

  const getAuthHeaders = useCallback(() => {
    const token = getAuthToken();
    const backendUrl = REACT_APP_BACKEND_URL();
    
    if (!token || !backendUrl) {
      throw new Error("Authentication token or backend URL not available.");
    }
    
    return {
      token,
      backendUrl,
      headers: { 'Authorization': `Bearer ${token}` }
    };
  }, []);

  const fetchRequestDetails = useCallback(async (requestId) => {
    if (!requestId) return null;
    
    try {
      const response = await new Promise((resolve, reject) => {
        const mockDispatch = (action) => {
          if (typeof action === 'function') {
            action(mockDispatch);
          } else if (action.type === REDUX_ACTION_TYPES.LOAD_REQUEST_SUCCESS) {
            resolve(action.payload);
          } else if (action.type === REDUX_ACTION_TYPES.LOAD_REQUEST_FAIL) {
            reject(new Error(action.payload));
          }
        };
        
        const thunk = getRequest(requestId);
        thunk(mockDispatch);
      });
      
      return response;
    } catch (error) {
      console.error("Error fetching request details:", error);
      throw error;
    }
  }, []);

  const fetchAccounts = useCallback(async (requestId) => {
    if (!requestId) throw new Error('Request ID is required');
    
    setLoading(true);
    try {
      const response = await getAPI().request({
        method: "get",
        url: `/api/FinDataRequest/${requestId}/accounts`,
      });
      
      return Array.isArray(response?.data) ? response.data : [];
    } catch (error) {
      console.error("Error fetching accounts:", error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  const saveAccounts = useCallback(async (requestId, accountsData) => {
    if (!requestId || !accountsData?.length) {
      throw new Error('Request ID and accounts data are required');
    }
    
    setLoading(true);
    try {
      await getAPI().request({
        method: "post",
        url: `/api/FinDataRequest/${requestId}/accounts`,
        data: accountsData,
      });
      
      return true;
    } catch (error) {
      console.error("Error saving accounts:", error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  const saveSingleAccount = useCallback(async (requestId, accountTitle, accountData) => {
    if (!requestId || !accountTitle || !accountData) {
      throw new Error('Request ID, account title and account data are required');
    }
    
    setLoading(true);
    try {
      // Кодируем accountTitle для URL
      const encodedAccountTitle = encodeURIComponent(accountTitle);

      await getAPI().request({
        method: "post",
        url: `/api/FinDataRequest/${requestId}/accounts/${encodedAccountTitle}`,
        data: accountData,
      });
      
      return true;
    } catch (error) {
      console.error("Error saving single account:", error);
      
      // Улучшаем информацию об ошибке
      if (error?.response?.status) {
        const status = error.response.status;
        const statusText = error.response.statusText || 'Unknown Error';
        const data = error.response.data;
        
        console.error(`API Error: ${status} ${statusText}`, data);
        
        if (status === 404) {
          throw new Error('Счет или запрос не найден');
        } else if (status === 403) {
          throw new Error('Недостаточно прав для сохранения');
        } else if (status === 400) {
          const message = typeof data === 'string' ? data : (data?.message || 'Некорректные данные');
          throw new Error(message);
        } else if (status >= 500) {
          throw new Error('Ошибка сервера. Попробуйте позже');
        }
      }
      
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    loading,
    fetchRequestDetails,
    fetchAccounts,
    saveAccounts,
    saveSingleAccount
  };
};